/**
 * Agentic canvas substrate — typed core. Spec: docs/specs/08-agentic-canvas.md.
 *
 * Every authoring action emits a typed graph mutation; the agent loop watches,
 * proposes Suggestions, and the canvas paints them as ghost overlays. Accept /
 * reject / expire are all typed events the Why-engine can replay.
 *
 * No literal thresholds in code — read from AGENT_CONFIG_DEFAULTS (CLAUDE.md §1).
 * No `any` (§2). Mirror lives at agent/foyr_agent/agent/types.py (§12).
 */
import { z } from "zod";

/** ─── Confidence policy (assistance not annoying — spec §UX 3) ───────── */
export const AgentConfigSchema = z.object({
  /** Master toggle. Off-state is sticky — UX rule 6. */
  agentOn: z.boolean(),
  /** Below this confidence the agent stays mute. */
  muteThreshold: z.number().min(0).max(1),
  /** Between mute and pulse the agent ghosts silently (no indicator). */
  pulseThreshold: z.number().min(0).max(1),
  /** How long a ghost survives before expiring (ms). */
  expireAfterMs: z.number().int().positive(),
});
export type AgentConfig = z.infer<typeof AgentConfigSchema>;

/** Defaults — read by tests + runtime. Adjust here, never inline. */
export const AGENT_CONFIG_DEFAULTS: AgentConfig = {
  agentOn: true,
  muteThreshold: 0.70,
  pulseThreshold: 0.85,
  expireAfterMs: 90_000,
};

/** ─── Typed mutation — every canvas action becomes one of these ─────── */
export const MutationKindSchema = z.enum(["create", "update", "delete"]);
export type MutationKind = z.infer<typeof MutationKindSchema>;

export const MutationOriginSchema = z.enum(["user", "agent", "import"]);
export type MutationOrigin = z.infer<typeof MutationOriginSchema>;

export const TypedMutationSchema = z.object({
  kind: MutationKindSchema,
  /** Ontology NodeKind (Room, Wall, Opening, Placement, …) from codegen.json. */
  nodeKind: z.string().min(1),
  /** Stable id of the graph subject. */
  subjectId: z.string().min(1),
  attrs: z.record(z.unknown()),
  ts: z.string(),
  origin: MutationOriginSchema,
});
export type TypedMutation = z.infer<typeof TypedMutationSchema>;

/** ─── Suggestion node ─────────────────────────────────────────────────── */
export const SuggestionStatusSchema = z.enum([
  "ghost", "accepted", "rejected", "expired",
]);
export type SuggestionStatus = z.infer<typeof SuggestionStatusSchema>;

export const SuggestionSchema = z.object({
  /** kind discriminator for graph-side tooling. */
  kind: z.literal("Suggestion"),
  id: z.string().min(1),
  status: SuggestionStatusSchema,
  /** [0, 1]. Below muteThreshold the suggestion would never be emitted. */
  confidence: z.number().min(0).max(1),
  /** What subject this is about — `{kind: "Placement", id: "p-42"}` etc. */
  about: z.object({ kind: z.string(), id: z.string() }),
  /** Rule IDs whose firing motivated this suggestion. Why-engine reads this. */
  wasInformedBy: z.array(z.string()),
  wasGeneratedBy: z.object({ kind: z.literal("Agent"), id: z.string() }),
  /** The mutation the agent is proposing the canvas should apply. */
  proposed: TypedMutationSchema,
  createdAt: z.string(),
  expiresAt: z.string(),
  /** True when confidence ≥ pulseThreshold — UI shows a single pulse mark. */
  pulse: z.boolean(),
});
export type Suggestion = z.infer<typeof SuggestionSchema>;

/** ─── Agent proposal (input to the gate) ─────────────────────────────── */
export const AgentProposalSchema = z.object({
  agentId: z.string().min(1),
  confidence: z.number().min(0).max(1),
  about: z.object({ kind: z.string(), id: z.string() }),
  wasInformedBy: z.array(z.string()),
  proposed: TypedMutationSchema,
});
export type AgentProposal = z.infer<typeof AgentProposalSchema>;

/** ─── Gate result — the pure decision: emit or not ───────────────────── */
export const EmitReasonSchema = z.enum([
  "agent-off", "below-mute-threshold", "emitted",
]);
export type EmitReason = z.infer<typeof EmitReasonSchema>;

export const EvaluationResultSchema = z.discriminatedUnion("emitted", [
  z.object({
    emitted: z.literal(false),
    reason: z.enum(["agent-off", "below-mute-threshold"]),
  }),
  z.object({
    emitted: z.literal(true),
    suggestion: SuggestionSchema,
  }),
]);
export type EvaluationResult = z.infer<typeof EvaluationResultSchema>;

/** ─── Pure core: gate a proposal against config ──────────────────────── */
export function evaluateProposal(
  proposal: AgentProposal,
  config: AgentConfig,
  now: Date = new Date(),
): EvaluationResult {
  if (!config.agentOn) return { emitted: false, reason: "agent-off" };
  if (proposal.confidence < config.muteThreshold) {
    return { emitted: false, reason: "below-mute-threshold" };
  }
  const expiresAt = new Date(now.getTime() + config.expireAfterMs);
  const suggestion: Suggestion = {
    kind: "Suggestion",
    id: deterministicId(proposal, now),
    status: "ghost",
    confidence: proposal.confidence,
    about: proposal.about,
    wasInformedBy: proposal.wasInformedBy,
    wasGeneratedBy: { kind: "Agent", id: proposal.agentId },
    proposed: proposal.proposed,
    createdAt: now.toISOString(),
    expiresAt: expiresAt.toISOString(),
    pulse: proposal.confidence >= config.pulseThreshold,
  };
  return { emitted: true, suggestion };
}

/** ─── Pure transition: ghost → accepted / rejected / expired ─────────── */
export const TransitionActionSchema = z.enum(["accept", "reject", "expire"]);
export type TransitionAction = z.infer<typeof TransitionActionSchema>;

export function transition(
  current: Suggestion,
  action: TransitionAction,
): Suggestion {
  if (current.status !== "ghost") return current;
  const next: SuggestionStatus =
    action === "accept" ? "accepted"
    : action === "reject" ? "rejected"
    : "expired";
  return { ...current, status: next };
}

/** Deterministic id so golden fixtures are reproducible across runtimes. */
function deterministicId(p: AgentProposal, now: Date): string {
  return `sug-${p.agentId}-${p.about.kind}-${p.about.id}-${now.getTime()}`;
}

/** ─── AgentBus pure helpers (golden-testable, side-effect-free) ──────── */

/** Stable dedup key — two proposals with the same (agentId, about, proposed
 *  subject+kind+nodeKind) collapse into one Suggestion. */
export function deriveDedupKey(p: AgentProposal): string {
  return [
    p.agentId,
    p.about.kind,
    p.about.id,
    p.proposed.kind,
    p.proposed.nodeKind,
    p.proposed.subjectId,
  ].join("|");
}

/** Has this Suggestion outlived its expiresAt window? */
export function isExpired(s: Suggestion, now: Date): boolean {
  return new Date(s.expiresAt).getTime() <= now.getTime();
}

/** When the agent toggles off (UX rule 6, sticky off-state), every open ghost
 *  flips to expired. Existing terminal states (accepted/rejected/expired) are
 *  untouched per UX rule 8 (honest history; nothing erased silently). */
export function closeOpen(
  suggestions: ReadonlyArray<Suggestion>,
  reason: "agent-off" | "manual-clear",
): ReadonlyArray<Suggestion> {
  void reason; // currently informational only — kept in signature for future audit log
  return suggestions.map((s) => (s.status === "ghost" ? { ...s, status: "expired" as const } : s));
}

/** ─── Canvas adapter (the seam between substrate and any canvas) ─────── */
export interface GhostHandle {
  readonly id: string;
  remove(): void;
  pulse(): void;
}

export interface SuggestionKeyHandlers {
  onAccept(s: Suggestion): void;
  onReject(s: Suggestion): void;
  onExplain(s: Suggestion): void;
}

export interface CanvasAdapter {
  emit(mutation: TypedMutation): void;
  paintGhost(suggestion: Suggestion): GhostHandle;
  bindKeys(handlers: SuggestionKeyHandlers): () => void;
  getSelection(): { kind: string; id: string } | null;
}
